import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Loading } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { AnnotatedText } from "@/components/AnnotatedText";
import { toast } from "sonner";
import { ArrowLeft, PenLine, Send, CheckCircle2, Star, Highlighter, FileText } from "lucide-react";
import { SCIENCE_RUBRICS, SCIENCE_FEEDBACK_LABELS } from "@/lib/scienceRubric";
import { fmtDue } from "@/lib/due";
import { FeedbackPdfView } from "@/components/FeedbackPdf";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function WritingTask() {
  const { hwId } = useParams();
  const navigate = useNavigate();
  const [hw, setHw] = useState(null);
  const [sub, setSub] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [activeNote, setActiveNote] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data: h } = await api.get(`/homework/${hwId}`);
        setHw(h);
        const { data: subs } = await api.get("/writing");
        const s = subs.find((x) => x.homework_id === hwId);
        if (s) { setSub(s); setText(s.response); }
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Could not load this task.");
        navigate(-1);
      }
    })();
  }, [hwId, navigate]);
  if (!hw) return <Loading />;

  const q = hw.questions[0];
  const prompt = q.prompt || q.text;
  const words = text.trim() ? text.trim().split(/\s+/).length : 0;
  const reviewed = sub?.status === "reviewed";
  const fileUrl = hw.file_url ? `${BACKEND_URL}${hw.file_url}` : null;

  const submit = async () => {
    if (words < 5) return toast.error("Write a bit more before submitting.");
    setBusy(true);
    try {
      const { data } = await api.post("/writing/submit", { homework_id: hwId, question_id: q.id, response: text });
      setSub(data);
      toast.success("Writing submitted — your tutor will provide feedback.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <div className="animate-fade-up mx-auto max-w-[1400px]">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#7C3AED] hover:underline" data-testid="writing-back-btn"><ArrowLeft className="h-4 w-4" /> Back</button>

      <div className="ember-card p-5 sm:p-6">
        <div className="flex items-center gap-2 text-sm font-semibold text-[#7C3AED]"><PenLine className="h-4 w-4" /> Writing Task{hw.due_date ? ` · ${fmtDue(hw.due_date)}` : ""}</div>
        <h1 className="mt-1 font-heading text-2xl font-extrabold text-[#1E1B2E]">{hw.title}</h1>
        {prompt && (
          <div className="mt-4 rounded-xl ember-gradient-soft p-4">
            <div className="text-xs font-bold uppercase tracking-wide text-[#5B21B6]">Prompt</div>
            <p className="mt-1 text-[15px] leading-relaxed text-[#1E1B2E]" data-testid="writing-prompt">{prompt}</p>
          </div>
        )}
      </div>

      {reviewed && sub.feedback && (() => { const sci = SCIENCE_RUBRICS[sub.feedback.writing_type]; return (
        <div className="ember-card mt-6 p-6" data-testid="writing-feedback-view">
          <div className="flex items-center gap-2"><span className="grid h-8 w-8 place-items-center rounded-lg bg-[#ECFDF5] text-[#059669]"><CheckCircle2 className="h-4 w-4" /></span><h2 className="font-heading text-lg font-bold text-[#1E1B2E]">Tutor feedback available</h2></div>
          {sci && <p className="mt-4 text-sm font-semibold text-[#1E1B2E]">Hi {sub.student_name?.split(" ")[0] || "there"}, thanks for the draft!</p>}
          {sub.feedback.strengths && <div className="mt-4"><div className="text-sm font-bold text-[#059669]">{sci ? SCIENCE_FEEDBACK_LABELS.strengths : "Strengths"}</div><p className="text-sm text-[#524B6B]">{sub.feedback.strengths}</p></div>}
          {sub.feedback.improvements && <div className="mt-3"><div className="text-sm font-bold text-[#E11D48]">{sci ? SCIENCE_FEEDBACK_LABELS.improvements : "Areas for improvement"}</div><p className="text-sm text-[#524B6B]">{sub.feedback.improvements}</p></div>}
          {sub.feedback.comment && <div className="mt-3 rounded-xl bg-[#F5F3FF] p-4 text-sm text-[#1E1B2E]">{sci && <div className="mb-1 font-bold text-[#5B21B6]">{SCIENCE_FEEDBACK_LABELS.comment}</div>}{sub.feedback.comment}</div>}
          {sub.response && (
            <div className="mt-4">
              <div className="mb-1 flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-[#5B21B6]"><Highlighter className="h-3.5 w-3.5 text-[#F59E0B]" /> Your response with tutor highlights</div>
              <AnnotatedText text={sub.response} annotations={sub.feedback.annotations || []} readOnly onClickAnnotation={setActiveNote} activeId={activeNote} />
            </div>
          )}
          {(sub.feedback.annotations || []).length > 0 && (
            <div className="mt-4" data-testid="student-inline-notes">
              <div className="mb-2 text-xs font-bold uppercase tracking-wide text-[#5B21B6]">Inline notes</div>
              <div className="space-y-2">
                {sub.feedback.annotations.map((a) => (
                  <div key={a.id} data-testid={`student-note-${a.id}`} onMouseEnter={() => setActiveNote(a.id)} onMouseLeave={() => setActiveNote(null)}
                    className="rounded-xl border border-[#EEEAF8] p-3">
                    <div className="truncate text-xs font-semibold text-[#B45309]">“{a.quote}”</div>
                    <div className="mt-0.5 text-sm text-[#1E1B2E]">{a.comment}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {sub.feedback.rubric && Object.keys(sub.feedback.rubric).length > 0 && (
            <div className="mt-4">
              <div className="mb-2 flex items-center gap-2">
                <span className="rounded-full ember-gradient px-3 py-1 text-sm font-extrabold text-white" data-testid="writing-score">{sub.feedback.score != null ? sub.feedback.score : Object.values(sub.feedback.rubric).reduce((a, b) => a + (b || 0), 0)}/{Object.keys(sub.feedback.rubric).length * 10}</span>
                {sub.feedback.writing_type && <span className="rounded-full bg-[#EDE9FE] px-2.5 py-1 text-xs font-bold uppercase tracking-wide text-[#5B21B6]">{sci ? sci.name : sub.feedback.writing_type}</span>}
              </div>
              <div className="grid gap-2 sm:grid-cols-2">
                {Object.entries(sub.feedback.rubric).map(([c, v]) => (
                  <div key={c} className="flex items-center justify-between rounded-xl border border-[#EEEAF8] px-3 py-2">
                    <span className="text-sm font-medium text-[#1E1B2E]">{c}</span>
                    <span className="inline-flex items-center gap-1 font-bold text-[#5B21B6]"><Star className="h-3.5 w-3.5" fill="#7C3AED" /> {v}/10</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          <FeedbackPdfView url={sub.feedback_pdf_url} />
          {sub.feedback.tutor_name && <p className="mt-4 text-xs text-[#948EAB]">Reviewed by {sub.feedback.tutor_name}</p>}
        </div>
      ); })()}

      {!reviewed && (
        <div className="mt-4 grid gap-4 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <div className="ember-card overflow-hidden p-0" data-testid="writing-pdf-panel">
              {fileUrl ? (
                <iframe title="writing task" src={`${fileUrl}#toolbar=1&view=FitH`} data-testid="writing-pdf" className="h-[78vh] w-full rounded-2xl" />
              ) : (
                <div className="grid h-[70vh] place-items-center p-6 text-center text-sm text-[#948EAB]">
                  <div>
                    <FileText className="mx-auto mb-2 h-7 w-7 text-[#C4BBE0]" />
                    No task document was attached. Follow the prompt above.
                  </div>
                </div>
              )}
            </div>
            <p className="mt-2 px-1 text-xs text-[#948EAB]">Read the task and instructions in the document, then write your response in the box on the right.</p>
          </div>

          <aside className="lg:col-span-5">
            <div className="ember-card flex flex-col p-5 lg:sticky lg:top-6" data-testid="writing-response-panel">
              <div className="mb-2 flex items-center justify-between">
                <h2 className="font-heading text-lg font-bold text-[#1E1B2E]">Your response</h2>
                <span className="text-sm font-semibold text-[#948EAB]" data-testid="word-count">{words} words</span>
              </div>
              <Textarea value={text} onChange={(e) => setText(e.target.value)} data-testid="writing-input"
                placeholder="Start writing your response here…" className="min-h-[52vh] flex-1 rounded-xl text-[15px] leading-relaxed" />
              {sub && <p className="mt-2 text-xs text-[#B45309]">Submitted — awaiting tutor feedback. You can resubmit an updated response.</p>}
              <Button onClick={submit} disabled={busy} data-testid="submit-writing-btn" className="mt-4 h-11 w-full rounded-xl ember-gradient font-bold shadow-lg shadow-violet-500/25">
                <Send className="mr-1.5 h-4 w-4" /> {busy ? "Submitting…" : sub ? "Resubmit writing" : "Submit & continue"}
              </Button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
